import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Center,
	chakra,
	HStack,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Spinner,
	Switch,
	Text,
	useDisclosure,
	useToast,
	VStack,
} from "@chakra-ui/react";
import apiFetch from "@wordpress/api-fetch";
import { __, sprintf } from "@wordpress/i18n";
import React, { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { localized } from "../../../../dashboard/utils";
import { useSearchParams } from "react-router-dom";
import { Ellipsis } from "../../../../base/components/Icons";
import { usePagination } from "../../../hooks/usePagination";

interface Template {
	id: number;
	ID: number;
	post_title?: string;
	post_thumbnail?: string;
	previewUrl: string;
	category?: { name: string; slug: string }[];
	tags?: string[];
	status: string;
	meta?: {
		_mzb_template?: string;
	};
	template_type?: string;
	type?: string;
	[key: string]: any;
}

interface TemplateListsProps {
	templateType?: string;
}

export const TemplatesPagination = (props: {
	totalPages: number;
	currentPage: number;
}) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { totalPages, currentPage } = props;

	const pagination = usePagination({
		total: totalPages,
		page: currentPage,
		onChange: (page: number) => {
			setSearchParams({ page: page.toString() });
		},
	});

	return (
		<Box mt="6">
			<HStack justify="center" gap="0">
				<Button
					isDisabled={pagination.active <= 1}
					bg="transparent"
					color={pagination.active <= 1 ? "#646464" : "#383838"}
					_hover={{
						bg: "transparent",
					}}
					onClick={() => {
						pagination.previous();
					}}
				>
					<chakra.svg
						stroke="currentColor"
						fill="none"
						strokeWidth="2"
						viewBox="0 0 24 24"
						strokeLinecap="round"
						strokeLinejoin="round"
						xmlns="http://www.w3.org/2000/svg"
						w="4"
						h="4"
					>
						<path d="m15 18-6-6 6-6"></path>
					</chakra.svg>
				</Button>
				<HStack>
					{pagination.range.map((r, i) => (
						<Button
							key={`${r}-${i}`}
							variant={
								pagination.active === r ? "solid" : "outline"
							}
							colorScheme="purple"
							borderColor="#6B46C1"
							isDisabled={pagination.active === r || r === "dots"}
							px="12px"
							py="6px"
							fontSize="14px"
							borderRadius="sm"
							bg={r === "dots" ? "transparent" : undefined}
							pointerEvents={
								r === "dots" || r === pagination.active
									? "none"
									: "auto"
							}
							_hover={{
								bg:
									r === "dots" || r === pagination.active
										? undefined
										: "gray.100",
							}}
							onClick={() => {
								if (r !== "dots") {
									pagination.setPage(Number(r));
									setSearchParams({ page: String(r) });
								}
							}}
						>
							{r === "dots" ? (
								<chakra.svg
									stroke="currentColor"
									fill="none"
									strokeWidth="2"
									viewBox="0 0 24 24"
									strokeLinecap="round"
									strokeLinejoin="round"
									xmlns="http://www.w3.org/2000/svg"
									w="4"
									h="4"
								>
									<circle cx="5" cy="12" r="1" />
									<circle cx="12" cy="12" r="1" />
									<circle cx="19" cy="12" r="1" />
								</chakra.svg>
							) : (
								r
							)}
						</Button>
					))}
				</HStack>
				<Button
					bg="transparent"
					_hover={{ bg: "transparent" }}
					isDisabled={pagination.active >= totalPages}
					color={
						pagination.active >= totalPages ? "#646464" : "#383838"
					}
					onClick={pagination.next}
				>
					<chakra.svg
						stroke="currentColor"
						fill="none"
						strokeWidth="2"
						viewBox="0 0 24 24"
						strokeLinecap="round"
						strokeLinejoin="round"
						xmlns="http://www.w3.org/2000/svg"
						w="4"
						h="4"
					>
						<path d="m9 18 6-6-6-6"></path>
					</chakra.svg>
				</Button>
			</HStack>
		</Box>
	);
};

export default function TemplateLists({ templateType }: TemplateListsProps) {
	const [searchParams, setSearchParams] = useSearchParams();
	const statusParam = searchParams.get("status");
	const status = statusParam ? [statusParam] : ["publish", "draft"];
	const page = searchParams.get("page") || "1";
	const search = searchParams.get("search") || undefined;

	const queryParams = {
		search,
		page: parseInt(page),
		per_page: 10,
		status,
		...(templateType &&
			templateType !== "all" && { template_type: templateType }),
	};

	const query = useQuery({
		queryKey: ["templates", queryParams, templateType],
		queryFn: async () => {
			const response = await apiFetch<Response>({
				path:
					"magazine-blocks/v1/builder-templates?" +
					Object.entries(queryParams)
						.reduce((acc, [key, value]) => {
							if (Array.isArray(value)) {
								value.forEach((v) => acc.push(`${key}[]=${v}`));
							} else if (value) {
								acc.push(`${key}=${value}`);
							}
							return acc;
						}, [] as string[])
						.join("&"),
				parse: false,
			});
			if (!(response.status >= 200 && response.status < 300))
				throw response;
			const data = (await response.json()) as Template[];
			const total = Number(response.headers.get("X-WP-Total") || 0);
			const totalPages = Number(
				response.headers.get("X-WP-TotalPages") || 0
			);

			return { data, total, totalPages };
		},
	});

	// Filter templates based on template_type
	const filteredTemplates = useMemo(() => {
		if (!query.data?.data) return [];

		// If templateType is not specified or is "all", return all templates
		if (!templateType || templateType === "all") {
			return query.data?.data;
		}

		// Filter templates that match the specified template type
		return query.data?.data.filter((template) => {
			const templateTypeValue =
				template.template_type || template.meta?._mzb_template;
			return templateTypeValue === templateType;
		});
	}, [query.data?.data, templateType]);

	const groupedTemplates = useMemo(() => {
		if (!filteredTemplates.length) return {};

		return filteredTemplates.reduce((acc, template) => {
			const type =
				template.template_type ||
				template.meta?._mzb_template ||
				"default";
			if (!acc[type]) {
				acc[type] = [];
			}
			acc[type].push(template);
			return acc;
		}, {} as Record<string, Template[]>);
	}, [filteredTemplates]);

	const activeTemplateMap = useMemo(() => {
		if (!query.data?.data?.length) return {};

		// Group published templates by template_type and find the one with the highest ID
		return query.data?.data
			.filter((t) => t.status === "publish")
			.reduce((acc, template) => {
				const type =
					template.template_type ||
					template.meta?._mzb_template ||
					"default";

				if (!acc[type] || template.id > acc[type].id) {
					acc[type] = template;
				}
				return acc;
			}, {} as Record<string, Template>);
	}, [query.data?.data]);

	const cancelRef = React.useRef(null);
	const toast = useToast();
	const queryClient = useQueryClient();

	const { isOpen, onOpen, onClose } = useDisclosure();
	const [deleteId, setDeleteId] = useState<number | null>();

	const deleteMutation = useMutation(
		(id: number) => {
			return apiFetch({
				path: `magazine-blocks/v1/builder-templates/${id}?force=true`,
				method: "DELETE",
			});
		},
		{
			onSuccess: () => {
				onClose();
				toast({
					title: __(
						"Template deleted successfully",
						"magazine-blocks"
					),
					status: "success",
					isClosable: true,
				});
				queryClient.refetchQueries(["templates"]);
			},
			onError(e: Error) {
				toast({
					title: e.message,
					status: "error",
					isClosable: true,
				});
			},
		}
	);

	const updateStatusMutation = useMutation(
		async ({
			id,
			status,
			type,
		}: {
			id: number;
			status: string;
			type: string;
		}) => {
			return apiFetch({
				path: `magazine-blocks/v1/builder-templates/${id}`,
				method: "POST",
				data: { status },
			});
		},
		{
			onSuccess: (_data, variables) => {
				queryClient.invalidateQueries({ queryKey: ["templates"] });

				toast({
					title: __("Status updated successfully", "magazine-blocks"),
					status: "success",
					isClosable: true,
				});
			},

			onError: (e: Error) => {
				toast({
					title: e.message,
					status: "error",
					isClosable: true,
				});
			},
		}
	);

	const formatTemplateType = (type: string): string => {
		return type
			.split("-")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(" ");
	};

	if (query.isLoading) {
		return (
			<Center h="200px">
				<VStack spacing={4}>
					<Spinner size="xl" />
					<Text>
						{sprintf(__("Loading templates...", "magazine-blocks"))}
					</Text>
				</VStack>
			</Center>
		);
	}

	if (query.isError) {
		return (
			<Center h="200px">
				<Text color="red.500">
					{query.error instanceof Error
						? query.error.message
						: __("Unknown error occurred!", "magazine-blocks")}
				</Text>
			</Center>
		);
	}

	// Show message if no templates match the filter
	if (filteredTemplates.length === 0) {
		return (
			<Center h="200px">
				<Text color="gray.500">
					{templateType && templateType !== "all"
						? sprintf(
								__(
									"No templates found for type: %s",
									"magazine-blocks"
								),
								templateType
						  )
						: __("No templates found", "magazine-blocks")}
				</Text>
			</Center>
		);
	}

	return (
		<>
			{Object.entries(groupedTemplates).map(([type, templates]) => (
				//@ts-ignore
				<>
					{type !== "all" && (
						<Text
							fontSize="13px"
							fontWeight="600"
							fontStyle="normal"
							margin="16px 0 0"
						>
							{type === "header" || type === "footer"
								? sprintf(
										__("Custom %s", "magazine-blocks"),
										formatTemplateType(type)
								  )
								: sprintf(
										__("Custom %s Page", "magazine-blocks"),
										formatTemplateType(type)
								  )}
						</Text>
					)}
					{templates.map((d) => {
						const isActive = activeTemplateMap[type]?.id === d.id;
						return (
							<>
								<HStack
									key={d.id || d.ID}
									justify="space-between"
									p="4"
									border="1px solid"
									borderColor="#e0e0e0"
									borderRadius="base"
									padding="12px"
								>
									<HStack flex="1" gap="0">
										<HStack>
											<span>
												<strong>
													{__(
														"Template: ",
														"magazine-blocks"
													)}
												</strong>{" "}
												{formatTemplateType(
													d?.title?.rendered
												)}{" "}
											</span>
											<span>
												<strong>
													{__(
														"ID: ",
														"magazine-blocks"
													)}
												</strong>{" "}
												{`#${d?.id || d?.ID}`}
											</span>
										</HStack>
										<chakra.svg
											stroke={
												d.status === "publish"
													? "#27ae60"
													: "#a7a7a7"
											}
											fill="none"
											strokeWidth="2"
											viewBox="0 0 24 24"
											strokeLinecap="round"
											strokeLinejoin="round"
											xmlns="http://www.w3.org/2000/svg"
											w="30px"
											h="30px"
										>
											<circle
												cx="12.1"
												cy="12.1"
												r="1"
											></circle>
										</chakra.svg>
										{isActive && (
											<chakra.span
												padding="2px 6px 4px 5px"
												color="#27AE60"
												borderRadius="2px"
												fontSize="10px"
												fontWeight="600"
												border="1px solid #E5F6EC"
												background="rgba(39, 174, 96, 0.12)"
												letterSpacing="0.2px"
											>
												{__(
													"Active",
													"magazine-blocks"
												)}
											</chakra.span>
										)}
									</HStack>
									<HStack>
										<Switch
											size="sm"
											isChecked={d.status === "publish"}
											isDisabled={
												updateStatusMutation.isLoading
											}
											onChange={(e) => {
												updateStatusMutation.mutate({
													id: d.id,
													status: e.target.checked
														? "publish"
														: "draft",
													type: type,
												});
											}}
											colorScheme="customPurple"
											sx={{
												"&:not([data-checked]) .chakra-switch__track":
													{
														backgroundColor:
															"#dbdbdb",
													},
											}}
										/>
										<Menu>
											<MenuButton
												as={Button}
												variant="icon"
												size="sm"
												onClick={(e) => {
													e.stopPropagation();
												}}
												padding={0}
												sx={{
													"& span": {
														flex: "unset",
													},
												}}
											>
												<Ellipsis />
											</MenuButton>
											<MenuList>
												<MenuItem
													onClick={() => {
														window.location.href =
															sprintf(
																localized.templateEditUrl,
																d.id
															);
													}}
													_hover={{
														background: "#FAF5FF",
													}}
													_active={{
														background: "#FAF5FF",
													}}
													_focus={{
														background: "#FAF5FF",
													}}
												>
													{__(
														"Edit",
														"magazine-blocks"
													)}
												</MenuItem>
												<MenuItem
													_hover={{
														background: "#FAF5FF",
													}}
													_active={{
														background: "#FAF5FF",
													}}
													_focus={{
														background: "#FAF5FF",
													}}
													onClick={() => {
														setDeleteId(d.id);
														onOpen();
													}}
												>
													{__(
														"Delete",
														"magazine-blocks"
													)}
												</MenuItem>
											</MenuList>
										</Menu>
									</HStack>
									<AlertDialog
										isOpen={isOpen}
										leastDestructiveRef={cancelRef}
										onClose={() => {
											setDeleteId(null);
											onClose();
										}}
										isCentered
									>
										<AlertDialogOverlay bg="rgba(0, 0, 0, 0.1)">
											<AlertDialogContent boxShadow="none">
												<AlertDialogHeader
													fontSize="lg"
													fontWeight="bold"
												>
													{sprintf(
														__(
															"Delete template #%d?",
															"magazine-blocks"
														),
														deleteId ?? 0
													)}
												</AlertDialogHeader>

												<AlertDialogBody>
													{__(
														"Are you sure? You can't undo this action afterwards.",
														"magazine-blocks"
													)}
												</AlertDialogBody>
												<AlertDialogFooter>
													<Button
														size="sm"
														fontWeight="normal"
														colorScheme="primary"
														variant="solid"
														ref={cancelRef}
														onClick={() => {
															if (deleteId) {
																deleteMutation.mutate(
																	deleteId
																);
															}
														}}
														_hover={{
															bg: "customPurple.500",
														}}
														isLoading={
															deleteMutation.isLoading
														}
														isDisabled={
															deleteMutation.isLoading
														}
													>
														{__(
															"Delete",
															"magazine-blocks"
														)}
													</Button>
													<Button
														size="sm"
														fontWeight="normal"
														colorScheme="primary"
														variant="outline"
														onClick={() => {
															setDeleteId(null);
															onClose();
														}}
														ml={3}
														isLoading={
															deleteMutation.isLoading
														}
														isDisabled={
															deleteMutation.isLoading
														}
														color="customPurple.500"
														borderColor="customPurple.500"
													>
														{__(
															"Cancel",
															"magazine-blocks"
														)}
													</Button>
												</AlertDialogFooter>
											</AlertDialogContent>
										</AlertDialogOverlay>
									</AlertDialog>
								</HStack>
							</>
						);
					})}
				</>
			))}
			{query.data && query.data.totalPages > 1 && (
				<TemplatesPagination
					currentPage={parseInt(page)}
					totalPages={query.data?.totalPages ?? 1}
				/>
			)}
		</>
	);
}
